import { useEffect, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { X, MapPin } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { useRealtimeLoadPings } from '@/hooks/use-realtime-loads';

// Fix default marker icons broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// The driver's live GPS ping.
export const liveIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

// Most recent pings first, oldest last (matches the `-recorded_at` sort
// they're fetched with) -- caller reverses when it needs chronological order.
export function fetchPings(loadId, limit) {
  return client.entities.LoadLocationPing.filter({ load_id: loadId }, '-recorded_at', limit);
}

// Ticks once a second so "Updated Xs ago" labels stay live without
// depending on a fresh ping arriving (the map re-renders regardless of
// whether anything actually changed server-side).
export function useTicker() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function formatAgo(recordedAt, now) {
  const seconds = Math.max(0, Math.round((now - new Date(recordedAt).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

export function FitBounds({ coords, singleZoom = 8 }) {
  const map = useMap();
  useEffect(() => {
    if (coords.length >= 2) {
      map.fitBounds(coords, { padding: [60, 60] });
    } else if (coords.length === 1) {
      map.setView(coords[0], singleZoom);
    }
  }, [coords, map, singleZoom]);
  return null;
}

// Single-load map -- driver's live position only, no planned route.
function SingleLoadMap({ load }) {
  const { carrierId } = useAuth();
  const [pings, setPings] = useState([]); // newest first
  const now = useTicker();
  const isTracking = load.status === 'In Transit';

  const refetchPings = useCallback(() => {
    fetchPings(load.id, 20).then(setPings).catch(() => {});
  }, [load.id]);

  useEffect(() => { refetchPings(); }, [refetchPings]);
  // Only worth an open realtime channel while the load can still produce
  // new pings -- a delivered load's trail is static.
  useRealtimeLoadPings(isTracking ? carrierId : null, refetchPings);

  const latest = pings[0];
  // Memoized on `pings` (not recomputed on the once-a-second ticker re-render
  // from useTicker) so FitBounds only re-fits when the location actually
  // changes -- otherwise a fresh array identity every second was resetting
  // any manual zoom/pan the second the driver's dot re-rendered.
  const livePos = useMemo(() => (pings[0] ? [pings[0].lat, pings[0].lng] : null), [pings]);
  const trail = useMemo(() => (pings.length > 1 ? [...pings].reverse().map(p => [p.lat, p.lng]) : null), [pings]);
  const validCoords = useMemo(() => (livePos ? [livePos] : []), [livePos]);

  return (
    <div style={{ height: '100%', position: 'relative' }}>
      {isTracking && !latest && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] px-3 py-1.5 rounded-full bg-black/80 border border-border text-xs text-muted-foreground">
          Live location unavailable — driver hasn't shared a location yet
        </div>
      )}
      <MapContainer key={load.id} center={[39.5, -98.35]} zoom={4} style={{ height: '100%', width: '100%', borderRadius: '0.75rem' }} zoomAnimation={false}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {validCoords.length > 0 && <FitBounds coords={validCoords} />}
        {trail && <Polyline positions={trail} color="#a855f7" weight={3} />}
        {livePos && (
          <Marker position={livePos} icon={liveIcon}>
            <Popup>
              <strong>{isTracking ? 'Live Position' : 'Last Known Position'}</strong><br />
              Updated {formatAgo(latest.recorded_at, now)}
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}

// Multi-load map for admin panel -- driver live positions only.
function MultiLoadMap({ loads }) {
  const { carrierId } = useAuth();
  const [latestPingByLoad, setLatestPingByLoad] = useState({}); // load_id -> ping
  const now = useTicker();
  const inTransitIds = loads.filter(l => l.status === 'In Transit').map(l => l.id).join(',');

  const refetchPings = useCallback(() => {
    const inTransit = loads.filter(l => l.status === 'In Transit');
    if (inTransit.length === 0) { setLatestPingByLoad({}); return; }
    Promise.all(inTransit.map(load => fetchPings(load.id, 1))).then(results => {
      const map = {};
      inTransit.forEach((load, i) => { if (results[i][0]) map[load.id] = results[i][0]; });
      setLatestPingByLoad(map);
    }).catch(() => {});
  }, [inTransitIds]);

  useEffect(() => { refetchPings(); }, [refetchPings]);
  useRealtimeLoadPings(carrierId, refetchPings);

  // Memoized on `latestPingByLoad` (not recomputed on the once-a-second
  // ticker re-render) so FitBounds only re-fits when the location data
  // actually changes -- see the matching comment in SingleLoadMap.
  const livePositions = useMemo(
    () => Object.values(latestPingByLoad).map(p => [p.lat, p.lng]),
    [latestPingByLoad]
  );

  return (
    <MapContainer center={[39.5, -98.35]} zoom={4} style={{ height: '100%', width: '100%', borderRadius: '0.75rem' }} zoomAnimation={false}>
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />
      {livePositions.length > 0 && <FitBounds coords={livePositions} />}
      {loads.map(load => {
        const ping = latestPingByLoad[load.id];
        if (!ping) return null;
        return (
          <Marker key={`live-${load.id}`} position={[ping.lat, ping.lng]} icon={liveIcon}>
            <Popup>
              <strong>#{load.load_number} · Live</strong><br />
              Updated {formatAgo(ping.recorded_at, now)}
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}

const Legend = () => (
  <div className="flex items-center gap-4 px-5 py-2 border-b border-border bg-white/3 text-xs text-muted-foreground">
    <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-pulse" /> Live position</div>
  </div>
);

// Modal wrapper — pass either `load` (single) or `loads` (multi)
// Pass `inline={true}` to render without modal overlay (e.g. embedded in a tab)
export default function LoadMapModal({ load, loads, onClose, inline = false }) {
  const isMulti = !!loads;
  const title = isMulti
    ? `Active Loads Map (${loads.length})`
    : `Load #${load?.load_number} — ${load?.origin} → ${load?.destination}`;

  const mapContent = (
    <>
      {!inline && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <h2 className="font-space font-semibold text-foreground text-sm truncate">{title}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      <Legend />
      <div style={{ height: inline ? 'calc(100% - 37px)' : 'calc(100% - 96px)' }}>
        {isMulti ? <MultiLoadMap loads={loads} /> : <SingleLoadMap load={load} />}
      </div>
    </>
  );

  if (inline) return <div style={{ height: '100%' }}>{mapContent}</div>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl z-10 overflow-hidden" style={{ height: '560px' }}>
        {mapContent}
      </div>
    </div>
  );
}
